import { useState, useCallback, useRef, useEffect } from "react";
import {
	checkName,
	checkEmailValidity,
	checkPhone,
	clearInputState,
} from "../utils";

export interface LoginFormFields {
	name: string;
	email: string;
	phone: string;
	recaptchaToken: string;
	recaptchaScore: number | null;
	countryCode: {
		code: string;
		name: string;
		phone: number;
	};
}

export interface FormErrors {
	name: string | null;
	email: string | null;
	phone: string | null;
	otp: string | null;
}

export interface FormFocus {
	name: boolean;
	email: boolean;
	phone: boolean;
	otp: boolean;
}

const defaultFields: LoginFormFields = {
	name: "",
	email: "",
	phone: "",
	recaptchaToken: "",
	recaptchaScore: null,
	countryCode: {
		code: "US",
		name: "United States",
		phone: 1,
	},
};

const defaultErrors: FormErrors = {
	name: null,
	email: null,
	phone: null,
	otp: null,
};

const defaultFocus: FormFocus = {
	name: false,
	email: false,
	phone: false,
	otp: false,
};

export interface UseLoginFormOptions {
	onInteraction?: () => void;
	onFieldChange?: (fieldName: string, value: LoginFormFields[keyof LoginFormFields]) => void;
}

/**
 * useLoginForm Hook
 *
 * Manages login form state, validation, and field updates
 *
 * @param options.onInteraction - Called on first field interaction (for funnel tracking)
 * @returns Object containing form state and handlers
 */
export const useLoginForm = (options?: UseLoginFormOptions) => {
	const [fields, setFields] = useState<LoginFormFields>(defaultFields);
	const [errors, setErrors] = useState<FormErrors>(defaultErrors);
	const [focus, setFocus] = useState<FormFocus>(defaultFocus);
	const [filled, setFilled] = useState<FormFocus>(defaultFocus);
	const [otpCode, setOTPCode] = useState<string>("");

	// Track if we've already called onInteraction
	const hasCalledInteraction = useRef(false);

	// Store options in refs to avoid dependency issues
	const onInteractionRef = useRef(options?.onInteraction);
	const onFieldChangeRef = useRef(options?.onFieldChange);
	// Keep refs in sync with the latest option callbacks. They are only read inside
	// callbacks (triggerInteraction, updateField), so syncing in an effect preserves behavior.
	useEffect(() => {
		onInteractionRef.current = options?.onInteraction;
		onFieldChangeRef.current = options?.onFieldChange;
	}, [options?.onInteraction, options?.onFieldChange]);

	const triggerInteraction = useCallback(() => {
		if (!hasCalledInteraction.current && onInteractionRef.current) {
			hasCalledInteraction.current = true;
			onInteractionRef.current();
		}
	}, []);

	/**
	 * Update a single field value
	 */
	const updateField = useCallback((fieldName: keyof LoginFormFields, value: LoginFormFields[keyof LoginFormFields]) => {
		triggerInteraction();
		onFieldChangeRef.current?.(fieldName, value);
		setFields(prev => ({ ...prev, [fieldName]: value }));
	}, [triggerInteraction]);

	/**
	 * Update multiple fields at once
	 */
	const updateFields = useCallback((updates: Partial<LoginFormFields>) => {
		setFields(prev => ({ ...prev, ...updates }));
	}, []);

	/**
	 * Set focus state for a field
	 */
	const setFieldFocus = useCallback((fieldName: keyof FormFocus, isFocused: boolean) => {
		setFocus(prev => ({ ...prev, [fieldName]: isFocused }));
	}, []);

	/**
	 * Set error for a field
	 */
	const setFieldError = useCallback((fieldName: keyof FormErrors, error: string | null) => {
		setErrors(prev => ({ ...prev, [fieldName]: error }));
	}, []);

	/**
	 * Clear all errors
	 */
	const clearErrors = useCallback(() => {
		setErrors(defaultErrors);
	}, []);

	/**
	 * Reset form to initial state
	 */
	const resetForm = useCallback(() => {
		setFields(defaultFields);
		setErrors(defaultErrors);
		setFocus(defaultFocus);
		setFilled(defaultFocus);
		setOTPCode("");
	}, []);

	/**
	 * Validate name field
	 */
	const validateName = useCallback(() => {
		return checkName(fields, errors, setErrors);
	}, [fields, errors]);

	/**
	 * Validate email field
	 */
	const validateEmail = useCallback(() => {
		return checkEmailValidity(fields.email, errors, setErrors);
	}, [fields.email, errors]);

	/**
	 * Validate phone field
	 */
	const validatePhone = useCallback(() => {
		return checkPhone(fields.phone, fields, errors, setErrors);
	}, [fields, errors]);

	/**
	 * Validate phone or email (for login)
	 */
	const validatePhoneOrEmail = useCallback(() => {
		const phoneOrEmail = fields.phone;
		return phoneOrEmail.includes("@")
			? checkEmailValidity(phoneOrEmail, errors, setErrors, "phone")
			: checkPhone(phoneOrEmail, fields, errors, setErrors);
	}, [fields, errors]);

	/**
	 * Validate all fields based on login view
	 */
	const validateAll = useCallback((loginView: string) => {
		const defFields = {
			name: null,
			email: null,
			phone: null,
			otp: null,
		};
		clearInputState(defFields, setErrors);

		if (loginView === "more-info") {
			const nameChecks = checkName(fields, errors, setErrors);
			if (!nameChecks) return false;
		}

		// The email is optional on more-info: a blank one saves the name alone. One
		// that IS typed still has to be a real address before a code is mailed to it.
		if (
			loginView === "more-info" && fields.email.trim() !== ""
		) {
			const emailChecks = checkEmailValidity(fields.email, errors, setErrors);
			if (!emailChecks) return false;
		}

		if (loginView === "mobile" || loginView === "login") {
			const checks = fields.phone.includes("@")
				? checkEmailValidity(fields.phone, errors, setErrors, "phone")
				: checkPhone(fields.phone, fields, errors, setErrors);

			if (!checks) return false;
		}

		return true;
	}, [fields, errors]);

	// Wrapped setters that trigger interaction tracking
	const setFieldsWithInteraction = useCallback((value: React.SetStateAction<LoginFormFields>) => {
		triggerInteraction();
		setFields(value);
	}, [triggerInteraction]);

	const setOTPCodeWithInteraction = useCallback((value: string) => {
		triggerInteraction();
		setOTPCode(value);
	}, [triggerInteraction]);

	return {
		fields,
		errors,
		focus,
		filled,
		otpCode,
		setFields: setFieldsWithInteraction,
		setErrors,
		setFocus,
		setFilled,
		setOTPCode: setOTPCodeWithInteraction,
		updateField,
		updateFields,
		setFieldFocus,
		setFieldError,
		clearErrors,
		resetForm,
		validateName,
		validateEmail,
		validatePhone,
		validatePhoneOrEmail,
		validateAll,
		triggerInteraction,
		hasInteracted: () => hasCalledInteraction.current,
	};
};
